import { AnimatePresence, motion } from 'framer-motion';
import { FileText, MousePointer2, ShieldCheck, Sparkles, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesaParcours } from '../../stores/usePesaParcours';

const AssetOverlay = () => {
  const { activeAsset, closeAsset, harvestStepData } = usePesaParcours();
  const { darkMode } = useTheme();
  const [selection, setSelection] = useState({ text: '', x: 0, y: 0, visible: false });
  const baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:5000';

  const handleTextSelection = useCallback(() => {
    const selectedText = window.getSelection()?.toString().trim();
    if (selectedText && selectedText.length > 5) {
      const range = window.getSelection()?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();
      if (rect) {
        setSelection({
          text: selectedText,
          x: rect.left + rect.width / 2,
          y: rect.top - 40,
          visible: true
        });
      }
    } else {
      setSelection(prev => ({ ...prev, visible: false }));
    }
  }, []);

  const captureSelection = () => {
    if (!selection.text) return;
    harvestStepData(`manual_extract_${Date.now()}`, {
      type: 'citation',
      content: selection.text,
      source: activeAsset?.titre || 'Asset inconnu'
    });
    setSelection(prev => ({ ...prev, visible: false }));
    window.getSelection()?.removeAllRanges();
  };

  return createPortal(
    <AnimatePresence>
      {activeAsset && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Mobile : drawer depuis le bas — Desktop : aligné à droite
          className="fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-end sm:p-6 bg-black/60 backdrop-blur-xl"
          onClick={closeAsset}
        >
          {/* Tooltip extraction texte — desktop uniquement */}
          <AnimatePresence>
            {selection.visible && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                style={{ left: selection.x, top: selection.y }}
                className="fixed z-[10000] -translate-x-1/2 hidden sm:flex items-center gap-2 bg-cyan-600/90 text-white px-5 py-2.5 rounded-full shadow-xl hover:bg-cyan-500 transition-all font-black text-xs uppercase tracking-widest backdrop-blur-sm border border-cyan-400/30"
                onClick={(e) => { e.stopPropagation(); captureSelection(); }}
              >
                <Sparkles size={14} className="animate-pulse" />
                RÉCOLTER LA PREUVE
              </motion.button>
            )}
          </AnimatePresence>

          {/* Panneau principal */}
          <motion.div
            // Mobile : slide depuis le bas — Desktop : slide depuis la droite
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            // Desktop override via variants
            whileInView={undefined}
            style={{}}
            className={`
              w-full sm:max-w-5xl
              h-[88vh] sm:h-[95vh]
              rounded-t-[2rem] sm:rounded-[3rem]
              border shadow-2xl overflow-hidden flex flex-col
              transition-colors duration-500 backdrop-blur-md
              ${darkMode
                ? 'bg-black/70 border-white/10 shadow-cyan-900/40'
                : 'bg-white/90 border-slate-200/80 shadow-slate-900/20'
              }
            `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle — mobile uniquement */}
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-white/20' : 'bg-slate-300'}`} />
            </div>

            {/* Header */}
            <div className={`h-14 sm:h-20 shrink-0 border-b px-4 sm:px-8 flex items-center justify-between ${
              darkMode ? 'border-white/5 bg-white/5' : 'border-slate-100 bg-slate-50/80'
            }`}>
              <div className="flex items-center gap-3 sm:gap-5 min-w-0">
                <div className={`p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl shrink-0 ${
                  darkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'
                }`}>
                  <FileText className="text-cyan-500" size={18} />
                </div>
                <div className="min-w-0">
                  <h4 className={`font-black uppercase tracking-tight sm:tracking-widest text-sm sm:text-lg truncate ${
                    darkMode ? 'text-white' : 'text-slate-900'
                  }`}>
                    {activeAsset.titre || activeAsset.nom || 'Ressource'}
                  </h4>
                  <div className="flex items-center gap-3 mt-0.5">
                    {/* Masqué sur mobile */}
                    <span className="hidden sm:flex items-center gap-1.5 text-[9px] text-cyan-400/90 font-mono uppercase tracking-[0.25em]">
                      <ShieldCheck size={10} /> CONSULTATION SÉCURISÉE
                    </span>
                    {activeAsset.isBonus && (
                      <span className="flex items-center gap-1.5 text-[9px] text-amber-400/90 font-mono uppercase tracking-[0.25em] bg-amber-500/10 px-2 py-0.5 rounded-full">
                        <Sparkles size={10} />
                        <span className="hidden sm:inline">BONUS & ASTUCE</span>
                        <span className="sm:hidden">BONUS</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={closeAsset}
                className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl transition-all shrink-0 ml-2 ${
                  darkMode
                    ? 'bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-400'
                    : 'bg-slate-200/80 hover:bg-red-500/10 text-slate-600 hover:text-red-600'
                }`}
              >
                <X size={18} />
              </button>
            </div>

            {/* Contenu principal */}
            <div
              className={`flex-1 overflow-hidden relative ${darkMode ? 'bg-black/80' : 'bg-slate-50/90'}`}
              onMouseUp={handleTextSelection}
            >
              {activeAsset?.filePath && activeAsset.filePath !== 'null' ? (
                // PDF existant
                <iframe
                  src={`${baseUrl}${activeAsset.filePath}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-full border-0"
                  title={activeAsset.titre || "Document"}
                />
              ) : activeAsset?.content ? (
                // Contenu texte riche
                <div className={`h-full w-full overflow-y-auto p-6 sm:p-12 selection:bg-cyan-500/30 selection:text-white ${
                  darkMode ? 'text-slate-200' : 'text-slate-800'
                }`}>
                  <div className="max-w-4xl mx-auto prose prose-invert max-w-none text-base sm:text-lg leading-relaxed font-serif">
                    <div dangerouslySetInnerHTML={{ __html: activeAsset.content }} />
                  </div>
                </div>
              ) : (
                // Fallback
                <div className="h-full flex flex-col items-center justify-center text-center px-8 sm:px-12">
                  <div className={`p-5 sm:p-6 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 ${
                    darkMode ? 'bg-white/5' : 'bg-slate-100/80'
                  }`}>
                    <Sparkles size={36} className="text-amber-400 animate-pulse" />
                  </div>

                  <h3 className={`text-xl sm:text-3xl font-black uppercase tracking-tight mb-3 sm:mb-4 ${
                    darkMode ? 'text-white' : 'text-slate-900'
                  }`}>
                    {activeAsset.titre || 'Ressource Bonus'}
                  </h3>

                  <p className={`text-sm sm:text-lg max-w-2xl mb-8 sm:mb-10 leading-relaxed ${
                    darkMode ? 'text-slate-300' : 'text-slate-600'
                  }`}>
                    {activeAsset.description || "Astuce complémentaire et pratique pour approfondir le sujet. Le fichier associé est en cours d'intégration."}
                  </p>

                  <div className="flex flex-col items-center gap-2 sm:gap-3 text-sm opacity-70">
                    <span className="flex items-center gap-2">
                      <FileText size={14} />
                      Type : {activeAsset.fileType || 'PDF bonus'}
                    </span>
                    <span className="text-xs font-mono">
                      ID : {activeAsset.id || activeAsset._id || '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — desktop uniquement (inutile sans souris) */}
            <div className={`h-14 shrink-0 hidden sm:flex items-center justify-between px-8 border-t text-[9px] font-mono uppercase tracking-widest ${
              darkMode
                ? 'bg-black/40 border-white/5 text-slate-400'
                : 'bg-white border-slate-100 text-slate-500'
            }`}>
              <div className="flex items-center gap-6">
                <span className="flex items-center gap-2">
                  <MousePointer2 size={12} />
                  Sélectionnez du texte pour extraire
                </span>
                <span>ID: {activeAsset?.id || activeAsset?._id || '—'}</span>
              </div>
              <span className="text-cyan-400/70">READY TO HARVEST</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AssetOverlay;